import { apiClient } from './apiClient';
import rpcAuth from './rpc/rpcAuth';

/**
 * Check if a company exists
 * Now uses RPC endpoint
 */
export async function checkCompanyExists(companySlug: string): Promise<{ exists: boolean }> {
  try {
    return await rpcAuth.checkCompanyAuth(companySlug);
  } catch {
    // On error, return true optimistically (fail open)
    return { exists: true };
  }
}

/**
 * Get tenant logo
 * Returns default logo (/crmLogo.png) if no logo is found (404) or on any error
 * This prevents console errors when companies don't have uploaded logos
 */
export async function getTenantLogo(company: string): Promise<{ url: string }> {
  try {
    const response = await apiClient.get(`/api/${company}/attachments/logo`, {
      validateStatus: (s) => s >= 200 && s < 500, // Accept 200-499 without throwing
    });
    
    // If 404 (no logo found), return default logo
    if (response.status === 404) {
      return { url: '/crmLogo.png' };
    }
    
    // If successful and has URL, return it
    if (response.status === 200 && response.data?.url) {
      return response.data;
    }
    
    // For any other status, return default logo
    return { url: '/crmLogo.png' };
  } catch (err: any) {
    // Silently handle all errors (network errors, timeouts, etc.) - return default logo
    // Don't log to console to prevent error spam
    return { url: '/crmLogo.png' };
  }
}

/**
 * Get company features
 * Note: This is handled by rpcSettings, not rpcAuth
 */
export async function getCompanyFeatures(company: string): Promise<{ enabledFeatures: string[] }> {
  const rpcSettings = (await import('./rpc/settings')).default;
  const { data } = await rpcSettings.getCompanyFeatures();
  return data;
}

/**
 * Logout
 * Now uses RPC endpoint
 */
export async function logout(company: string, reason: string = 'unknown'): Promise<void> {
  // 🔥 CRITICAL DEBUG: Log every logout call to track who is calling it
  const stack = new Error().stack;
  const callerLines = stack?.split('\n').slice(0, 6) || [];
  
/*   console.log('[FRONTEND LOGOUT CALLED]', {
    company,
    reason,
    from: 'logout-function',
    callerStack: callerLines,
    timestamp: new Date().toISOString(),
  }); */
  
  // CRITICAL: Set manual logout flag BEFORE calling API to prevent interceptor from handling 401
  // This ensures that any 401 errors during logout are ignored
  if (typeof window !== 'undefined') {
    const logoutTimestamp = Date.now().toString();
    sessionStorage.setItem('manualLogout', 'true');
    localStorage.setItem('manualLogout', 'true');
    sessionStorage.setItem('manualLogoutTime', logoutTimestamp);
    localStorage.setItem('manualLogoutTime', logoutTimestamp);
    
    // Clear justLoggedIn flag to prevent interceptor from ignoring 401 errors
    sessionStorage.removeItem('justLoggedIn');
    sessionStorage.removeItem('justLoggedInTime');
    sessionStorage.removeItem('justLoggedInCompany');
  }
  
  try {
    await rpcAuth.logout(company);
  } catch (err: any) {
    // If logout API call fails with 401, that's expected - token is already invalid
    // Don't throw error, just log it
    if (err?.response?.status === 401) {
      // Expected when token is already invalid
    } else {
      console.error('[FRONTEND LOGOUT ERROR]', {
        company,
        reason,
        error: err?.message,
        status: err?.response?.status,
      });
    }
    // Don't throw - continue with logout cleanup even if API call fails
  }
}

/**
 * Login (superadmin or regular)
 * Now uses RPC endpoint
 * Note: Superadmin login still goes through /api/superadmin/login (separate endpoint)
 */
export async function login(
  isSuperadmin: boolean,
  company: string,
  payload: {
    email: string;
    password: string;
    companyNumber?: string;
    device_id?: string;
    device_name?: string;
    forceLogout?: boolean;
  }
): Promise<any> {
  if (isSuperadmin) {
    // Superadmin login still uses REST endpoint (separate from auth RPC)
    const { data } = await apiClient.post(`/api/superadmin/login`, payload);
    return data;
  }
  
  // Regular login uses RPC
  return await rpcAuth.login(payload, company);
}

/**
 * Verify OTP (superadmin or regular)
 * Now uses RPC endpoint
 * Note: Superadmin OTP still goes through /api/superadmin/verify-otp (separate endpoint)
 * 
 * PHASE 3 STEP A: Added device_id and device_name support for session tracking
 */
export async function verifyOtp(
  isSuperadmin: boolean,
  company: string,
  payload: { 
    email: string; 
    otp: string;
    device_id?: string;
    device_name?: string;
  }
): Promise<any> {
  if (isSuperadmin) {
    // Superadmin OTP verification still uses REST endpoint (separate from auth RPC)
    const { data } = await apiClient.post(`/api/superadmin/verify-otp`, payload);
    return data;
  }
  
  // Regular OTP verification uses RPC (public endpoint, no company needed)
  return await rpcAuth.verifyOtp(payload, '');
}

/**
 * Verify 2FA
 * Now uses RPC endpoint
 */
export async function verify2FA(
  company: string,
  payload: {
    email: string;
    otp?: string;
    backupCode?: string;
    device_id: string;
    device_name: string;
  }
): Promise<any> {
  return await rpcAuth.verify2FA(payload, company);
}

/**
 * Resend 2FA OTP
 * Now uses RPC endpoint
 */
export async function resend2FA(company: string, payload: { email: string }): Promise<any> {
  return await rpcAuth.resend2FA(payload, company);
}

/**
 * Send reset password OTP
 * Now uses RPC endpoint
 */
export async function sendResetOtp(
  isSuperadmin: boolean,
  company: string,
  payload: { email: string }
): Promise<void> {
  // Both superadmin and regular users can use the same RPC endpoint
  // The endpoint handles both cases (with or without company)
  await rpcAuth.sendResetOtp(payload, isSuperadmin ? undefined : company);
}

/**
 * Reset password
 * Now uses RPC endpoint
 */
export async function resetPassword(
  isSuperadmin: boolean,
  company: string,
  payload: {
    email: string;
    otp: string;
    newPassword: string;
  }
): Promise<void> {
  // Both superadmin and regular users can use the same RPC endpoint
  // The endpoint handles both cases (with or without company)
  try {
    await rpcAuth.resetPassword(payload, isSuperadmin ? undefined : company);
  } catch (e: any) {
    // Fallback: try global endpoint if company-specific endpoint fails
    if (!isSuperadmin && e?.response?.status === 404) {
      await rpcAuth.resetPassword(payload, undefined);
    } else {
      throw e;
    }
  }
}

